'use client';

import type { ComponentProps, ReactNode } from 'react';
import { cn } from '@ifixit/ui/lib/utils';
import { Label } from './label';

function Field({ className, ...props }: ComponentProps<'div'>) {
   return <div role="group" data-slot="field" className={cn(className)} {...props} />;
}

function FieldSet({ className, ...props }: ComponentProps<'fieldset'>) {
   return <fieldset data-slot="field-set" className={cn(className)} {...props} />;
}

function FieldLegend({
   className,
   variant = 'legend',
   ...props
}: ComponentProps<'legend'> & { variant?: 'legend' | 'label' }) {
   return (
      <legend
         data-slot="field-legend"
         data-variant={variant}
         className={cn(className)}
         {...props}
      />
   );
}

function FieldGroup({ className, ...props }: ComponentProps<'div'>) {
   return <div data-slot="field-group" className={cn(className)} {...props} />;
}

function FieldContent({ className, ...props }: ComponentProps<'div'>) {
   return <div data-slot="field-content" className={cn(className)} {...props} />;
}

function FieldLabel({ className, ...props }: ComponentProps<typeof Label>) {
   return <Label data-slot="field-label" className={cn(className)} {...props} />;
}

function FieldTitle({ className, ...props }: ComponentProps<'div'>) {
   return <div data-slot="field-title" className={cn(className)} {...props} />;
}

function FieldDescription({ className, ...props }: ComponentProps<'p'>) {
   return <p data-slot="field-description" className={cn(className)} {...props} />;
}

function FieldError({
   className,
   children,
   errors,
   ...props
}: ComponentProps<'div'> & {
   errors?: Array<{ message?: string } | undefined>;
}) {
   let content: ReactNode = children;

   if (!content) {
      if (!errors?.length) return null;
      const uniqueErrors = [...new Map(errors.map(error => [error?.message, error])).values()];
      content =
         uniqueErrors.length === 1 ? (
            uniqueErrors[0]?.message
         ) : (
            <ul className="ml-4 flex list-disc flex-col gap-1">
               {uniqueErrors.map(
                  (error, index) => error?.message && <li key={index}>{error.message}</li>
               )}
            </ul>
         );
   }

   if (!content) return null;

   return (
      <div role="alert" data-slot="field-error" className={cn(className)} {...props}>
         {content}
      </div>
   );
}

export {
   Field,
   FieldContent,
   FieldDescription,
   FieldError,
   FieldGroup,
   FieldLabel,
   FieldLegend,
   FieldSet,
   FieldTitle,
};
